import React from 'react';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Play, Square, Trees } from 'lucide-react';
import AmbientCredits from '../AmbientCredits';

/**
 * Ambient Scene — a standalone looping soundscape (Garden, Fireside, Night
 * Watch…). Plays independently of narration. User picks a preset and taps
 * Play to start the loop, Stop to end it.
 */
const AmbientScenePanel = ({
  sceneId,
  onSceneChange,
  scenes,
  scenePlaying,
  onTogglePlay,
  sceneVolume,
  onSceneVolumeChange,
  activeSceneLayers,
  es = false,
}) => (
  <div className="border-t border-muted pt-4" data-testid="ambient-scene-panel">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Trees className="w-4 h-4 text-primary" />
        <div className="flex flex-col">
          <span className="text-sm font-medium">{es ? 'Escena ambiental (independiente)' : 'Ambient scene (standalone)'}</span>
          <span className="text-xs text-muted-foreground">
            {es ? 'Un paisaje sonoro en bucle que puedes reproducir para orar o leer' : 'A looping soundscape you can play on its own for prayer or reading'}
          </span>
        </div>
      </div>
    </div>

    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm">{es ? 'Escena' : 'Scene'}</span>
        <Select value={sceneId} onValueChange={onSceneChange}>
          <SelectTrigger className="w-56" data-testid="scene-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {scenes.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.icon} {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-between">
        <Button
          size="sm"
          variant={scenePlaying ? 'default' : 'outline'}
          onClick={onTogglePlay}
          className={scenePlaying ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
          data-testid="scene-play-stop-btn"
        >
          {scenePlaying ? (
            <>
              <Square className="w-4 h-4 mr-2" />
              {es ? 'Detener escena' : 'Stop scene'}
            </>
          ) : (
            <>
              <Play className="w-4 h-4 mr-2" />
              {es ? 'Reproducir escena' : 'Play scene'}
            </>
          )}
        </Button>
        {scenePlaying && (
          <span className="text-xs text-muted-foreground italic">{es ? 'En bucle independiente' : 'Looping independently'}</span>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{es ? 'Volumen de escena' : 'Scene volume'}</span>
          <span className="text-xs text-muted-foreground">{Math.round(sceneVolume * 100)}%</span>
        </div>
        <Slider
          value={[sceneVolume]}
          onValueChange={(v) => onSceneVolumeChange(v[0])}
          max={1}
          step={0.05}
          data-testid="scene-volume-slider"
        />
      </div>

      {scenePlaying && activeSceneLayers.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="active-scene-layers">
          {activeSceneLayers.map((e) => (
            <Badge key={e} variant="secondary" className="text-xs">
              {e}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex items-center justify-end -mt-1">
        <AmbientCredits es={es} />
      </div>
    </div>
  </div>
);

export default AmbientScenePanel;
